import { COLORS, WEAPON_CATEGORY_COLOR } from '@/config/colors';
import { css, fillOutlined, circle, shade } from './draw-utils';
import type { ProceduralDef } from '../types';
import type { WeaponCategory } from '@/config/weapons';

/** Plasma sphere — violet orb, bright core (category colour, docs/12 E4). */
export const bulletPlayerPlasma: ProceduralDef = {
  key: 'bullet/player/plasma',
  width: 56,
  height: 56,
  anchorX: 0.5,
  anchorY: 0.5,
  frames: 1,
  draw(ctx, w, h) {
    const c = w / 2;
    // Plasma bloom: layered radial falloff feeds the additive blend (G8)
    const halo = ctx.createRadialGradient(c, c, (c - 10) * 0.4, c, c, c);
    halo.addColorStop(0, 'rgba(166, 107, 255, 0.55)');
    halo.addColorStop(1, 'rgba(166, 107, 255, 0)');
    circle(ctx, c, c, c);
    ctx.fillStyle = halo;
    ctx.fill();
    circle(ctx, c, c, c - 10);
    fillOutlined(ctx, COLORS.weaponPlasma, 4);
    circle(ctx, c, c, (c - 10) * 0.5);
    ctx.fillStyle = css(shade(COLORS.weaponPlasma, 1.5));
    ctx.fill();
    circle(ctx, c - 4, c - 5, (c - 10) * 0.2);
    ctx.fillStyle = 'rgba(255,255,255,0.9)';
    ctx.fill();
    void h;
  },
};

/** Laser needle — thin and long, +X-facing. */
export const bulletPlayerLaser: ProceduralDef = {
  key: 'bullet/player/laser',
  width: 64,
  height: 16,
  anchorX: 0.5,
  anchorY: 0.5,
  frames: 1,
  draw(ctx, w, h) {
    const cy = h / 2;
    // Beam haze along the whole needle (G8)
    const haze = ctx.createLinearGradient(0, 0, w, 0);
    haze.addColorStop(0, 'rgba(182, 242, 58, 0)');
    haze.addColorStop(0.7, 'rgba(182, 242, 58, 0.45)');
    haze.addColorStop(1, 'rgba(255, 252, 240, 0.6)');
    ctx.beginPath();
    ctx.ellipse(w / 2, cy, w / 2 - 1, h / 2 - 1, 0, 0, Math.PI * 2);
    ctx.fillStyle = haze;
    ctx.fill();
    // Outer lime body — pointed tip (kept clear of armor/legendary amber)
    ctx.beginPath();
    ctx.moveTo(4, cy - 3);
    ctx.lineTo(w - 14, cy - 4);
    ctx.lineTo(w - 3, cy);
    ctx.lineTo(w - 14, cy + 4);
    ctx.lineTo(4, cy + 3);
    ctx.closePath();
    ctx.fillStyle = css(COLORS.weaponLaser);
    ctx.fill();
    // Hot core
    ctx.beginPath();
    ctx.moveTo(10, cy - 1.5);
    ctx.lineTo(w - 16, cy - 2);
    ctx.lineTo(w - 7, cy);
    ctx.lineTo(w - 16, cy + 2);
    ctx.lineTo(10, cy + 1.5);
    ctx.closePath();
    ctx.fillStyle = 'rgba(255, 252, 240, 0.95)';
    ctx.fill();
  },
};

/** Bomb projectile — heavy dark body + fuse light. */
export const bulletPlayerBomb: ProceduralDef = {
  key: 'bullet/player/bomb',
  width: 48,
  height: 48,
  anchorX: 0.5,
  anchorY: 0.5,
  frames: 1,
  draw(ctx, w, h) {
    const c = w / 2;
    // Fuse heat shimmer (G8)
    const halo = ctx.createRadialGradient(c + 7, c - 7, 1, c + 7, c - 7, 13);
    halo.addColorStop(0, 'rgba(255, 92, 184, 0.6)');
    halo.addColorStop(1, 'rgba(255, 92, 184, 0)');
    circle(ctx, c + 7, c - 7, 13);
    ctx.fillStyle = halo;
    ctx.fill();
    circle(ctx, c, c, c - 6);
    fillOutlined(ctx, 0x2a2433, 5);
    // Top highlight
    circle(ctx, c - 5, c - 5, (c - 6) * 0.4);
    ctx.fillStyle = css(shade(0x2a2433, 1.6));
    ctx.fill();
    // Fuse light — magenta, never the enemy orange
    circle(ctx, c + 7, c - 7, 4);
    ctx.fillStyle = css(COLORS.weaponBomb);
    ctx.fill();
    circle(ctx, c + 7, c - 7, 2);
    ctx.fillStyle = css(shade(COLORS.weaponBomb, 1.4));
    ctx.fill();
    void h;
  },
};

/**
 * On-hull weapon pod, one per equipped socket (docs/09): chunky top-view
 * silhouette per category in its WEAPON_CATEGORY_COLOR with the dark
 * outline, sized to stay readable at gameplay zoom. Barrel points +X.
 */
function makeMount(category: WeaponCategory): ProceduralDef {
  return {
    key: `player/mount-${category}`,
    width: 48,
    height: 32,
    anchorX: 0.5,
    anchorY: 0.5,
    frames: 1,
    draw(ctx, w, h) {
      const cx = w / 2;
      const cy = h / 2;
      const c = WEAPON_CATEGORY_COLOR[category];

      switch (category) {
        case 'bullet': {
          // Gun pod: squat body + one thick barrel
          ctx.beginPath();
          ctx.roundRect(cx + 4, cy - 4, 15, 8, 2);
          fillOutlined(ctx, shade(c, 0.72), 3);
          ctx.beginPath();
          ctx.roundRect(cx - 14, cy - 8, 22, 16, 6);
          fillOutlined(ctx, c, 3.5);
          break;
        }
        case 'plasma': {
          // Capsule pod with a glowing orb at the muzzle
          ctx.beginPath();
          ctx.roundRect(cx - 15, cy - 7, 24, 14, 7);
          fillOutlined(ctx, shade(c, 0.75), 3.5);
          circle(ctx, cx + 9, cy, 7.5);
          fillOutlined(ctx, c, 3);
          circle(ctx, cx + 7, cy - 2.5, 2.5);
          ctx.fillStyle = 'rgba(255,255,255,0.9)';
          ctx.fill();
          break;
        }
        case 'laser': {
          // Long emitter rail ending in a prism tip
          ctx.beginPath();
          ctx.roundRect(cx - 16, cy - 5, 28, 10, 4);
          fillOutlined(ctx, shade(c, 0.78), 3.5);
          ctx.beginPath();
          ctx.moveTo(cx + 10, cy - 8);
          ctx.lineTo(cx + 20, cy);
          ctx.lineTo(cx + 10, cy + 8);
          ctx.lineTo(cx + 6, cy);
          ctx.closePath();
          fillOutlined(ctx, c, 3);
          break;
        }
        case 'bomb': {
          // Round drum + stubby launch tube
          ctx.beginPath();
          ctx.roundRect(cx + 2, cy - 6, 14, 12, 3);
          fillOutlined(ctx, shade(c, 0.7), 3);
          circle(ctx, cx - 5, cy, 10);
          fillOutlined(ctx, c, 3.5);
          circle(ctx, cx - 5, cy, 3.5);
          ctx.fillStyle = css(COLORS.outline);
          ctx.fill();
          break;
        }
      }
      void h;
    },
  };
}

export const mountBullet = makeMount('bullet');
export const mountPlasma = makeMount('plasma');
export const mountLaser = makeMount('laser');
export const mountBomb = makeMount('bomb');

// Weapon pickup icons moved to per-weapon glyphs: weapon-glyphs.ts (ui/weapon-<id>).
